import { Link } from "react-router-dom";

/**
 * Broker ↔ External User cross-link primitives.
 *
 * Two DISTINCT records that must always reach each other, with consistent
 * labels + placement across every surface that names a broker:
 *  - Broker Record (CRM)        → /brokers/{brokerId}            "View broker record"
 *  - External User account      → /admin/portal-accounts/{id}    "View portal account"
 *
 * The displayed "broker name" is the broker company name, so the name itself
 * links to the broker record; the portal account is reached via the explicit
 * "View portal account" link. When a broker has no portal account, the portal
 * link renders as a muted "No portal account" state (never a broken link or a
 * raw GUID).
 */

const linkClass = "text-small text-gold-shadow hover:underline";
const mutedClass = "text-small text-gray-mid italic";

/** Link to the CRM Broker record. When `label` is omitted, renders the
 *  standard "View broker record" affordance; pass the resolved broker name to
 *  turn the name itself into the link. */
export function BrokerRecordLink({
  brokerId,
  label,
  className,
  onClick,
}: {
  brokerId: string;
  label?: string | null;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  return (
    <Link
      to={`/brokers/${brokerId}`}
      className={className ?? linkClass}
      onClick={onClick}
      data-testid="link-broker-record"
    >
      {label ?? "View broker record"}
    </Link>
  );
}

/** Link to the staff-visible External User (portal) account. Renders a muted
 *  "No portal account" state when the broker has no linked portal login. */
export function PortalAccountLink({
  portalUserId,
  className,
  onClick,
}: {
  portalUserId: string | null | undefined;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  if (!portalUserId) {
    return (
      <span className={mutedClass} data-testid="link-portal-account-none">
        No portal account
      </span>
    );
  }
  return (
    <Link
      to={`/admin/portal-accounts/${portalUserId}`}
      className={className ?? linkClass}
      onClick={onClick}
      data-testid="link-portal-account"
    >
      View portal account
    </Link>
  );
}
